import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Active-project store — F2.1 multi-app support.
 *
 * Holds the currently-selected project. Every dashboard view
 * subscribes to this store and refetches when `activeProjectId`
 * changes. The UUID is also threaded as `X-Project-Id` on every
 * API call via `shaktiApi` so the tenant middleware can validate
 * it matches the caller's org.
 *
 * Persisted to sessionStorage so reloads don't forget the last
 * selection (but a new tab starts fresh).
 */
export interface ProjectSummary {
  id: string;
  name: string;
  slug: string;
  organization_id: string;
}

interface ProjectState {
  activeProjectId: string | null;
  projects: ProjectSummary[];
  setActiveProject: (id: string | null) => void;
  setProjects: (projects: ProjectSummary[]) => void;
  clear: () => void;
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set) => ({
      activeProjectId: null,
      projects: [],
      setActiveProject: (id) => set({ activeProjectId: id }),
      setProjects: (projects) => {
        set((prev) => {
          // Auto-select the first project if nothing is active yet.
          const activeStillValid =
            prev.activeProjectId !== null &&
            projects.some((p) => p.id === prev.activeProjectId);
          const nextActive = activeStillValid
            ? prev.activeProjectId
            : (projects[0]?.id ?? null);
          return { projects, activeProjectId: nextActive };
        });
      },
      clear: () => set({ activeProjectId: null, projects: [] }),
    }),
    {
      name: 'shakti-project-store',
      storage: {
        getItem: (key) => {
          const raw = sessionStorage.getItem(key);
          return raw ? JSON.parse(raw) : null;
        },
        setItem: (key, value) => sessionStorage.setItem(key, JSON.stringify(value)),
        removeItem: (key) => sessionStorage.removeItem(key),
      },
    },
  ),
);

/**
 * N5.5 (round-6) — hook that re-fires `effect` whenever the active
 * project changes. Previously 13 of 16 routes had empty-dep
 * `useEffect(() => …, [])` arrays and rendered stale data after a
 * project switch. This helper centralises the project-scoped
 * dependency and also aborts any in-flight AbortController when the
 * active project changes so the prior project's response can never
 * leak into the new view.
 *
 * Usage:
 *   useProjectScopedEffect((signal) => {
 *     fetch('/v1/projects/...').then(...)
 *   }, [otherDep])
 */
export function useProjectScopedEffect(
  effect: (signal: AbortSignal, projectId: string | null) => void | (() => void),
  extraDeps: unknown[] = [],
): void {
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Abort any previous in-flight request before launching the new
    // one. This is what keeps stale responses from rendering after a
    // rapid project switch.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const cleanup = effect(controller.signal, activeProjectId);

    return () => {
      controller.abort();
      if (typeof cleanup === 'function') cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, ...extraDeps]);
}
